export interface PathCandidate {
  /** Exact matched substring including any :line:col suffix. */
  raw: string;
  /** Path with the :line[:col] suffix removed and trailing punctuation trimmed. */
  path: string;
  line?: number;
  col?: number;
  /**
   * Offsets of `raw` in the source text — the span covers the path PLUS any
   * :line:col suffix (NOT just `path`). Terminal/markdown callers underline
   * this whole span.
   */
  start: number;
  end: number;
}

// A path-shaped token: optional ~ / . / .. prefix, optional leading slash,
// then one-or-more "segment/" groups, a final segment, and an optional
// :line[:col] suffix. The character class deliberately excludes spaces,
// quotes, backticks, parens, '<', '>' and ':' so the match stops at token
// boundaries and at HTML tags when reused by the markdown linkifier.
const SEG = "[A-Za-z0-9_.@\\-]";
const PATH_SOURCE = `(?:~|\\.\\.|\\.)?/?(?:${SEG}+/)+${SEG}+(?::\\d+(?::\\d+)?)?`;

/** Fresh regex per call — /g state must not be shared across invocations. */
export function makePathRegex(): RegExp {
  return new RegExp(PATH_SOURCE, "g");
}

const KNOWN_PREFIX = /^(?:~\/|\.\.?\/|\/)/;
const HAS_EXTENSION = /\.[A-Za-z0-9]+$/;
// A path token is a fragment of a larger token (URL tail, etc.) if the char
// immediately before it is itself path/url-ish.
const BAD_PREFIX_CHAR = /[A-Za-z0-9:/~.@\-]/;

/**
 * Validate + normalize a raw match into a PathCandidate, or null if it is not
 * a plausible file path. Shared by extractPathCandidates and the markdown
 * linkifier so both apply identical rules.
 */
export function parseCandidate(
  raw: string,
  start: number,
  precededBy: string | null
): PathCandidate | null {
  if (precededBy && BAD_PREFIX_CHAR.test(precededBy)) return null;

  let body = raw;
  let line: number | undefined;
  let col: number | undefined;
  const suffix = body.match(/:(\d+)(?::(\d+))?$/);
  if (suffix) {
    line = Number(suffix[1]);
    if (suffix[2] !== undefined) col = Number(suffix[2]);
    body = body.slice(0, suffix.index);
  }
  body = body.replace(/[.,;]+$/, "");

  if (!HAS_EXTENSION.test(body) && !KNOWN_PREFIX.test(body)) return null;
  if (!body.includes("/")) return null;

  return { raw, path: body, line, col, start, end: start + raw.length };
}

export function extractPathCandidates(text: string): PathCandidate[] {
  if (!text) return [];
  const re = makePathRegex();
  const out: PathCandidate[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const precededBy = m.index > 0 ? text[m.index - 1] : null;
    const candidate = parseCandidate(m[0], m.index, precededBy);
    if (candidate) out.push(candidate);
  }
  return out;
}
